import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Download, Mail, Calendar, TrendingUp } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';

const Reports = () => {
  const handleExportPDF = (reportType) => {
    toast({
      title: "Generating Report",
      description: `Your ${reportType} report is being generated...`
    });
    
    // Simulate PDF generation
    setTimeout(() => {
      toast({
        title: "Download Ready!",
        description: `${reportType} report has been downloaded`
      });
    }, 2000);
  };

  const handleExportCSV = (dataType) => {
    toast({
      title: "Exporting Data",
      description: `Exporting ${dataType} data as CSV...`
    });
    
    setTimeout(() => {
      toast({
        title: "Export Complete!",
        description: `${dataType} CSV file downloaded`
      });
    }, 1500);
  };

  const handleEmailReport = (reportType) => {
    toast({
      title: "Sending Email",
      description: `${reportType} report will be sent to your email`
    });
  };

  const reportTemplates = [
    {
      name: 'Weekly Summary',
      description: 'Glucose, meals, medications, and exercise overview',
      period: 'Last 7 days',
      icon: Calendar,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      name: 'Monthly Analysis',
      description: 'Comprehensive health data and trends',
      period: 'Last 30 days',
      icon: TrendingUp,
      color: 'from-emerald-500 to-teal-500'
    },
    {
      name: 'Quarterly Review',
      description: 'Long-term progress and pattern analysis',
      period: 'Last 90 days',
      icon: FileText,
      color: 'from-purple-500 to-indigo-500'
    },
    {
      name: "Doctor's Report",
      description: 'Medical-grade report for healthcare providers',
      period: 'Custom range',
      icon: FileText,
      color: 'from-orange-500 to-red-500'
    }
  ];

  const dataExports = [
    { name: 'Glucose Readings', description: 'All blood sugar measurements', icon: TrendingUp },
    { name: 'Meal Log', description: 'Food diary with nutrition data', icon: FileText },
    { name: 'Medication History', description: 'Med and insulin log', icon: FileText },
    { name: 'Exercise Activity', description: 'All workout sessions', icon: FileText },
    { name: 'Health Vitals', description: 'Weight and blood pressure', icon: FileText },
    { name: 'Complete Dataset', description: 'All your health data', icon: Download }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl p-8 text-white shadow-lg">
        <h2 className="text-3xl font-bold mb-2">Reports & Export</h2>
        <p className="text-cyan-100 text-lg">Generate comprehensive reports and export your data</p>
      </div>

      <Tabs defaultValue="reports" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="reports">PDF Reports</TabsTrigger>
          <TabsTrigger value="exports">Data Export</TabsTrigger>
        </TabsList>

        {/* PDF Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <Card className="shadow-md border-0 bg-gradient-to-br from-blue-50 to-cyan-50">
            <CardHeader>
              <CardTitle>Professional Reports</CardTitle>
              <CardDescription>
                Generate beautifully formatted PDF reports to share with your healthcare team
              </CardDescription>
            </CardHeader>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            {reportTemplates.map((report, index) => {
              const Icon = report.icon;
              return (
                <Card key={index} className="shadow-md border-0 hover:shadow-xl transition-all">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-12 h-12 bg-gradient-to-br ${report.color} rounded-lg flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <Badge className="bg-gray-100 text-gray-700">{report.period}</Badge>
                    </div>
                    <CardTitle className="text-xl">{report.name}</CardTitle>
                    <CardDescription>{report.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => handleExportPDF(report.name)}
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        PDF
                      </Button>
                      <Button
                        onClick={() => handleEmailReport(report.name)}
                        variant="outline"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Report Preview Info */}
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle>What's Included in Reports?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900">Health Metrics</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Average glucose levels with trends</li>
                    <li>• Time in range analysis</li>
                    <li>• Medication adherence rates</li>
                    <li>• Exercise activity summary</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900">Visualizations</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Glucose trend charts</li>
                    <li>• Daily pattern graphs</li>
                    <li>• Nutrition breakdown</li>
                    <li>• Progress comparison</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Export Tab */}
        <TabsContent value="exports" className="space-y-6">
          <Card className="shadow-md border-0 bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardHeader>
              <CardTitle>Export Your Data</CardTitle>
              <CardDescription>
                Download your health data in CSV format for analysis in Excel, Google Sheets, or other tools
              </CardDescription>
            </CardHeader>
          </Card>

          <div className="grid md:grid-cols-3 gap-6">
            {dataExports.map((dataType, index) => {
              const Icon = dataType.icon;
              return (
                <Card key={index} className="shadow-md border-0 hover:shadow-lg transition-all">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-lg">{dataType.name}</CardTitle>
                    <CardDescription className="text-sm">{dataType.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      onClick={() => handleExportCSV(dataType.name)}
                      variant="outline"
                      className="w-full"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Export Info */}
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle>Data Export Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">CSV Format</h4>
                  <p className="text-sm text-gray-600">
                    All exports are in standard CSV (comma-separated values) format, compatible with Excel, Google Sheets, and most data analysis tools.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Download className="w-5 h-5 text-emerald-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Complete Data</h4>
                  <p className="text-sm text-gray-600">
                    Exports include all your historical data, timestamps, and associated metadata for comprehensive analysis.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-purple-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Data Privacy</h4>
                  <p className="text-sm text-gray-600">
                    Your data is encrypted during export and only accessible to you. We never share your health information.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
